// Inbox (DESIGN §3; LangChain Agent Inbox + Linear triage): one decision queue — proposals, review notes to clear, sign-offs,
// learning, healing repairs, research and currency proposals. Each verb is one catalogue command through its typed form:
// Accept · Edit then accept · Reject with reason · Reply with guidance · Snooze.
// Keys: j / k next / previous, a accept, e edit then accept, r reject with reason, g reply with guidance, s snooze.
import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { DecisionPanel, type DecisionMode as Mode } from "../app/DecisionPanel";
import { useDecisions } from "../app/decisions";
import { EmptyState, ErrorState, Kbd, LoadingRows, PageHeader, StatusPill } from "../components";
import type { DecisionItem } from "../data/types";
import { titleCase } from "../lib/format";
import { useHotkeys } from "../lib/hooks";

export function InboxPage() {
  const { state, open, snoozed, snooze, unsnooze } = useDecisions();
  const [params, setParams] = useSearchParams();
  const [view, setView] = useState<"open" | "snoozed">("open");
  const [kind, setKind] = useState<string | null>(null);
  const [sent, setSent] = useState<Record<string, string>>({});
  const base = view === "open" ? open : snoozed;
  const kinds = useMemo(() => { const m = new Map<string, number>(); for (const d of base) m.set(d.kind, (m.get(d.kind) ?? 0) + 1); return m; }, [base]);
  const list = kind ? base.filter((d) => d.kind === kind) : base;
  // Selection is local state (instant for j/k); the URL follows it so a decision can be linked (?item=…).
  const [sel, setSel] = useState<string | null>(() => params.get("item"));
  const selectedId = sel && list.some((d) => d.id === sel) ? sel : list[0]?.id;
  const idx = list.findIndex((d) => d.id === selectedId);
  const item = idx >= 0 ? list[idx] : undefined;
  const select = (d: DecisionItem | undefined) => { if (d) { setSel(d.id); setParams({ item: d.id }, { replace: true }); } };
  const [mode, setMode] = useState<Mode>(null);
  const [nudge, setNudge] = useState(0);            // bumps to ask the detail pane to run "accept"
  useEffect(() => setMode(null), [selectedId]);
  const rowRefs = useRef(new Map<string, HTMLLIElement>());
  useEffect(() => { if (selectedId) rowRefs.current.get(selectedId)?.scrollIntoView?.({ block: "nearest" }); }, [selectedId]);

  useHotkeys({
    j: () => select(list[Math.min(idx + 1, list.length - 1)]),
    k: () => select(list[Math.max(idx - 1, 0)]),
    a: () => item && setNudge((n) => n + 1),
    e: () => item && setMode("edit"),
    r: () => item?.decision.reject && setMode("reject"),
    g: () => item?.decision.guidance && setMode("guidance"),
    s: () => { if (item && view === "open") { const next = list[idx + 1] ?? list[idx - 1]; snooze(item.id); select(next); } },
  }, mode === null);

  const after = (d: DecisionItem, status: string) => {
    setSent((s) => ({ ...s, [d.id]: status }));
    setMode(null);
    select(list[idx + 1] ?? list[idx - 1]);
  };

  return (
    <div className="mx-auto max-w-6xl">
      <PageHeader title="Inbox" subtitle="Every decision waiting on you, in one queue. Your choice goes to the engine, which runs it through its gates."
        actions={<span className="hidden items-center gap-1 text-xs text-muted md:flex"><Kbd>j</Kbd><Kbd>k</Kbd> move · <Kbd>a</Kbd> accept · <Kbd>e</Kbd> edit · <Kbd>r</Kbd> reject · <Kbd>g</Kbd> guidance · <Kbd>s</Kbd> snooze</span>} />
      <div className="mb-3 flex flex-wrap items-center gap-1.5" role="group" aria-label="Filter decisions">
        <Chip on={view === "open" && kind === null} onClick={() => { setView("open"); setKind(null); }} label="Waiting" count={open.length} />
        {[...kinds.entries()].map(([k, n]) => <Chip key={k} on={kind === k} onClick={() => setKind(kind === k ? null : k)} label={titleCase(k)} count={n} />)}
        <Chip on={view === "snoozed"} onClick={() => { setView(view === "snoozed" ? "open" : "snoozed"); setKind(null); }} label="Snoozed" count={snoozed.length} />
      </div>
      {state.error ? <ErrorState error={state.error} onRetry={state.reload} /> : !state.data ? <LoadingRows /> : list.length === 0 ? (
        <EmptyState title={view === "open" ? "Nothing waits on you." : "Nothing snoozed."}>Agents and the engine post decisions here when a run or a gate needs a person.</EmptyState>
      ) : (
        <div className="grid gap-4 md:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]">
          <ul role="listbox" aria-label="Decisions" aria-activedescendant={item ? `dec-${item.id}` : undefined} tabIndex={0}
            className="max-h-[70vh] divide-y divide-line overflow-y-auto rounded-lg border border-line bg-raised focus-visible:outline-none"
            onKeyDown={(e) => { if (e.key === "ArrowDown") { e.preventDefault(); select(list[Math.min(idx + 1, list.length - 1)]); } else if (e.key === "ArrowUp") { e.preventDefault(); select(list[Math.max(idx - 1, 0)]); } }}>
            {list.map((d) => (
              <li key={d.id} id={`dec-${d.id}`} ref={(el) => { if (el) rowRefs.current.set(d.id, el); }} role="option" aria-selected={d.id === selectedId}
                onClick={() => select(d)} className={`cursor-pointer px-3 py-2 text-sm ${d.id === selectedId ? "bg-brand/10" : "hover:bg-sunken"}`}>
                <div className="flex items-center gap-1.5">
                  <StatusPill state={sent[d.id] ?? (d.byAgent ? "proposed" : "waiting")} label={sent[d.id] ? "Sent to the engine" : undefined} />
                  <span className="text-xs text-muted">{titleCase(d.kind)}</span>
                </div>
                <p className="mt-0.5 line-clamp-2">{d.what}</p>
                <p className="text-xs text-muted">{d.engagementName ?? "Firm"}</p>
              </li>
            ))}
          </ul>
          {item && <DecisionPanel key={item.id} d={item} mode={mode} setMode={setMode} nudge={nudge} snoozedView={view === "snoozed"} keys
            onSnooze={() => { snooze(item.id); select(list[idx + 1] ?? list[idx - 1]); }} onUnsnooze={() => unsnooze(item.id)} onSent={(s) => after(item, s)} />}
        </div>
      )}
    </div>
  );
}

function Chip({ on, onClick, label, count }: { on: boolean; onClick: () => void; label: string; count: number }) {
  return (
    <button type="button" aria-pressed={on} onClick={onClick} aria-label={`${label}, ${count}`}
      className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs ${on ? "border-brand bg-brand/10 font-semibold text-brand" : "border-line text-muted hover:text-ink"}`}>
      {label}<span className="tabular-nums">{count}</span>
    </button>
  );
}
